// ----------------------------------------------------------------------------
// Helper: Selling Plan Frequency
// ----------------------------------------------------------------------------
// Maps human-readable selling plan text ("Monthly", "Every Other Month",
// "1 Month", …) to a numeric delivery frequency in months, and finds the plan
// matching a requested frequency.
//
// Used by:
// - snippets/product-form/selling-plan-input (?frequency= deep links on PDP)
// - sections/subscription-cards (direct Add to Cart mode)
// ----------------------------------------------------------------------------

/**
 * Parse a frequency (in months) out of selling plan text.
 * @param {string} value  Plan/option text, e.g. "Monthly", "Every Other Month", "2 months"
 * @returns {number|null} Frequency in months, or null when unrecognized
 */
export var extractFrequencyFromText = value => {
  var normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'monthly') return 1;
  if (normalized === 'bimonthly') return 2;
  if (normalized === 'every other month') return 2;
  if (normalized === '1' || normalized === '2') return Number(normalized);
  var monthMatch = normalized.match(/\b([1-9]\d?)[\s-]*(month|months|mo)\b/);
  if (monthMatch) return Number(monthMatch[1]);
  return null;
};

/**
 * Find the selling plan matching a requested frequency.
 * @param {Array<Object>} plans  Plans shaped { id, name, group_name, options: [text, …] }
 * @param {number} requestedFrequency  Frequency in months (1 = monthly, 2 = every other month)
 * @returns {string|number|null} The matching plan id, or null
 */
export var findPlanIdByFrequency = (plans, requestedFrequency) => {
  var _match$id;
  if (!requestedFrequency || !Array.isArray(plans)) return null;
  var match = plans.find(plan => {
    var optionValues = Array.isArray(plan === null || plan === void 0 ? void 0 : plan.options) ? plan.options : [];
    var candidates = [...optionValues, plan === null || plan === void 0 ? void 0 : plan.name, plan === null || plan === void 0 ? void 0 : plan.group_name];
    return candidates.some(value => extractFrequencyFromText(value) === requestedFrequency);
  });
  return (_match$id = match === null || match === void 0 ? void 0 : match.id) !== null && _match$id !== void 0 ? _match$id : null;
};